import React, { useEffect, useRef, useState } from "react";
import {
  FaSignInAlt,
  FaSignOutAlt,
  FaHourglassHalf,
  FaCheckCircle,
} from "react-icons/fa";
import { toast } from "react-toastify";
import styles from "./EmployeeAttendance.module.css";
import LeaveRequestModal from "./LeaveRequestModal";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import type { RootState, AppDispatch } from "../Services/Store";
import { useAuth } from "../Contexts/AuthContext";
import {
  fetchAttendanceSummary,
  fetchAttendanceMyDetails, 
  fetchAttendanceLogs,
  fetchLeaves,
  // fetchTasks,
  checkIn,
  checkOut,
  resetAttendance,
} from "../Services/Slices/attendanceSlice";
import { formatAttendanceTime } from "./attendanceFormat";

const EmployeeAttendance: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const userId = useSelector((state: RootState) => state.auth.user?.user_id);
  const username =
    useSelector((state: RootState) => state.auth.user?.username) || "User";
  const { authLoading } = useAuth();

  const {
    summary,
    myDetails,
    logs,
    logsPagination,
    leaveStats,
    recentLeaves,
    // tasks,           // 🔕 commented – Task Management disabled per request
    loading,
    logsLoading,
    logsLoadingMore,
    leaveLoading,
    // tasksLoading,    // 🔕 commented – Task Management disabled per request
  } = useSelector((state: RootState) => state.attendance);
  useNavigate();

  const [currentTime, setCurrentTime] = useState(new Date());
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const logsSentinelRef = useRef<HTMLDivElement | null>(null);
  const logsFetchingRef = useRef(false);
  
  // Clear previous employee's attendance when user changes
  useEffect(() => {
    dispatch(resetAttendance());
    localStorage.removeItem("last_check_in_iso");
  }, [userId, dispatch]);

  useEffect(() => {
    if (!authLoading && typeof userId === "number" && userId > 0) {
      dispatch(fetchAttendanceSummary());
      dispatch(fetchAttendanceMyDetails());
      dispatch(fetchAttendanceLogs({ page: 1 }));
      dispatch(fetchLeaves());
      // dispatch(fetchTasks(userId)); // 🔕 commented – Task Management disabled per request
    }
    // eslint-disable-next-line
  }, [authLoading, userId]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleCheckIn = async () => {
    try {
      await dispatch(checkIn()).unwrap();
      toast.success("Checked In Successfully!");
      dispatch(fetchAttendanceSummary());
      dispatch(fetchAttendanceMyDetails());
    } catch {
      toast.error("Failed to check in.");
    }
  };

  const handleCheckOut = async () => {
    try {
      await dispatch(checkOut()).unwrap();
      toast.success("Checked Out Successfully!");
      dispatch(fetchAttendanceSummary());
      dispatch(fetchAttendanceMyDetails());
    } catch {
      toast.error("Failed to check out.");
    }
  };

  const hasMoreLogs =
    logsPagination.current_page < logsPagination.total_pages;

  useEffect(() => {
    const node = logsSentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0]?.isIntersecting &&
          hasMoreLogs &&
          !logsLoading &&
          !logsLoadingMore &&
          !logsFetchingRef.current
        ) {
          logsFetchingRef.current = true;
          dispatch(
            fetchAttendanceLogs({
              page: logsPagination.current_page + 1,
              append: true,
            })
          ).finally(() => {
            logsFetchingRef.current = false;
          });
        }
      },
      { root: null, rootMargin: "240px 0px", threshold: 0 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [
    dispatch,
    hasMoreLogs,
    logsLoading,
    logsLoadingMore,
    logsPagination.current_page,
    authLoading,
    userId,
  ]);

  // === Helper functions for formatting ===
  const formatTime = (time: string | null | undefined) =>
    formatAttendanceTime(time, "--:--");
  const formatDate = (date: Date) =>
    date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  const formatLeaveDate = (dateStr: string) => {
    if (!dateStr) return "--";
    const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr.trim());
    const d = ymd
      ? new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]))
      : new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr;
    return `${d.getDate().toString().padStart(2, "0")} ${d.toLocaleString(
      "default",
      { month: "short" }
    )}, ${d.getFullYear()}`;
  };
  const calcLeaveDays = (from: string, to: string) => {
    if (!from || !to) return "--";
    const days =
      Math.floor(
        (new Date(to).getTime() - new Date(from).getTime()) /
          (1000 * 60 * 60 * 24)
      ) + 1;
    return days > 0 ? `${days} Day${days > 1 ? "s" : ""}` : "--";
  };

  // Utility to title-case status strings like "PRESENT" -> "Present"
  const humanizeStatus = (s: string | undefined) => {
    if (!s) return "--";
    const up = s.toString().toUpperCase();
    if (up === "PRESENT") return "Present";
    if (up === "ABSENT") return "Absent";
    if (up === "ON_LEAVE") return "On Leave";
    if (up === "LATE") return "Late";
    return up.charAt(0) + up.slice(1).toLowerCase().replace("_", " ");
  };
  
  // Prefer this user's API details first (never shared browser cache)
  type AnyLog = {
    date?: string;
    check_in_time?: string | null;
    check_out_time?: string | null;
    check_in?: string | null;
    check_out?: string | null;
    work_hours?: string | number | null;
    status?: string;
  };

  // Find today's log (tolerant)
  const logsAny = (Array.isArray(logs) ? logs : []) as unknown as AnyLog[];
  const todayIso = new Date().toISOString().slice(0, 10);
  const todaysLog = logsAny.find(l => {
    try {
      const d = new Date(l?.date ?? "");
      return d.toISOString().slice(0, 10) === todayIso;
    } catch {
      return false;
    }
  });

  const loginTime =
    myDetails?.check_in_time ??
    summary?.login_time ??
    todaysLog?.check_in_time ??
    todaysLog?.check_in ??
    null;

  const logoutTime =
    myDetails?.check_out_time ??
    summary?.logout_time ??
    todaysLog?.check_out_time ??
    todaysLog?.check_out ??
    null;
  
  const hasCheckedIn = !!loginTime;
  const hasCheckedOut = !!logoutTime;

  const derivedCanCheckIn = !hasCheckedIn;
  const derivedCanCheckOut = hasCheckedIn && !hasCheckedOut;
  const toDateSafe = (iso?: string | null) => {
    if (!iso) return null;
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d;
  };
  const formatDurationHHMMSS = (ms: number) => {
    if (!isFinite(ms) || ms < 0) return "--";
    const totalSec = Math.floor(ms / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    return `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}:${String(s).padStart(2,"0")}`;
  };
  const mdIn  = myDetails?.check_in_time ?? null;
  const mdOut = myDetails?.check_out_time ?? null;
  let computedWorkHoursFromMyDetails: string | null = null;
  if (mdIn) {
    const start = toDateSafe(mdIn);
    const end   = toDateSafe(mdOut) ?? currentTime; // live tick until check-out
    if (start && end) {
      const diff = end.getTime() - start.getTime();
      if (diff >= 0) computedWorkHoursFromMyDetails = formatDurationHHMMSS(diff);
    }
  }

  if (authLoading || !userId) {
    return (
      <div style={{ padding: 100, textAlign: "center", fontSize: 18 }}>
        🔐 Loading your dashboard...
      </div>
    );
  }
  return (
    <div className={styles.pageWrapper}>
      {/* Header Row */}
      <div className={styles.headerRow}>
        <img
          src="/Assets/employee.png"
          className={styles.avatar}
          alt="avatar"
        />
        <span className={styles.greet}>
          Hii {username} welcome back !!!!
        </span>
        <div className={styles.dateTime}>
          <span>{formatDate(currentTime)}</span>
          <span>
            {currentTime.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            })}
          </span>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className={styles.mainGrid}>
        {/* LEFT: Summary, Attendance, Logs */}
        <div className={styles.leftCol}>
          {/* --- Today's Summary --- */}
          <div className={styles.sectionTitle}>Today's Summary</div>
          <div className={styles.summaryCard}>
            {loading || !summary ? (
              <div style={{ padding: 20, textAlign: "center", width: "100%" }}>
                Loading summary...
              </div>
            ) : (
              <>
                <div className={styles.summaryItem}>
                  <div
                    className={styles.summaryIconWrap}
                    style={{ background: "#E5F7ED" }}
                  >
                    <FaSignInAlt size={32} color="#28C76F" />
                  </div>
                  <div className={styles.summaryLabel}>Login Time</div>
                  <div className={styles.summaryValue}>
                    {formatTime(loginTime)}
                  </div>
                </div>
                <div className={styles.summaryItem}>
                  <div
                    className={styles.summaryIconWrap}
                    style={{ background: "#FFEAEA" }}
                  >
                    <FaSignOutAlt size={32} color="#EA5455" />
                  </div>
                  <div className={styles.summaryLabel}>Logout Time</div>
                  <div className={styles.summaryValue}>
                    {formatTime(logoutTime)}
                  </div>
                </div>
                <div className={styles.summaryItem}>
                  <div
                    className={styles.summaryIconWrap}
                    style={{ background: "#EAF0FF" }}
                  >
                    <FaHourglassHalf size={32} color="#7367F0" />
                  </div>
                  <div className={styles.summaryLabel}>Working Hours</div>
                  <div className={styles.summaryValue}>
                    {/* [CHANGED] prefer ONLY my-details computed hours */}
                    {computedWorkHoursFromMyDetails ?? summary.work_hours}
                  </div>
                </div>
                <div className={styles.summaryItem}>
                  <div
                    className={styles.summaryIconWrap}
                    style={{ background: "#E7F9F2" }}
                  >
                    <FaCheckCircle size={32} color="#28C76F" />
                  </div>
                  <div className={styles.summaryLabel}>Status</div>
                  <div
                    className={styles.summaryValue}
                    style={{
                      color:
                        (summary.status || "").toLowerCase() === "present"
                          ? "#28C76F"
                          : "#EA5455",
                    }}
                  >
                    {summary.status}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* --- Today's Attendance --- */}
          <div className={styles.todaysAttendance}>
            <div className={styles.sectionTitleRow}>
              <div>
                <div className={styles.sectionTitle}>Today's Attendance</div>
                <div className={styles.todayDate}>
                  {formatDate(currentTime)}
                </div>
              </div>
              <div className={styles.currentTime}>
                {currentTime.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                  hour12: true,
                })}
                <span className={styles.curTimeLabel}>Current Time</span>
              </div>
            </div>
            <div className={styles.attBtnRow}>
              <button
                className={styles.checkInBtn}
                onClick={handleCheckIn}
                disabled={!derivedCanCheckIn || loading}
              >
                <FaSignInAlt /> Check In
              </button>

              <button
                className={styles.checkOutBtn}
                onClick={handleCheckOut}
                disabled={!derivedCanCheckOut || loading}
              >
                <FaSignOutAlt /> Check Out
              </button>

            </div>
            <div style={{ marginTop: 10, color: "#888", fontSize: 14 }}>
              {derivedCanCheckIn && !derivedCanCheckOut
                ? "You can check in now."
                : !derivedCanCheckIn && derivedCanCheckOut
                ? "Don't forget to check out at end of day."
                : !derivedCanCheckIn && !derivedCanCheckOut && logoutTime
                ? "You have completed today's attendance."
                : ""}
            </div>
          </div>

          {/* --- Attendance History / Logs Table --- */}
          <div className={styles.sectionTitle} style={{ marginTop: 32 }}>
            Attendance History
          </div>
          <div className={styles.attendanceHistoryWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Check In</th>
                  <th>Check Out</th>
                  <th>Work Hours</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {logsLoading && logs.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: "center" }}>
                      Loading...
                    </td>
                  </tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: "center" }}>
                      No Data
                    </td>
                  </tr>
                ) : (
                  logs.map((log: any, i: number) => (
                    <tr key={`${log.date || "log"}-${i}`}>
                      <td>{formatLeaveDate(log.date)}</td>
                      {/* 👇 use API fields check_in_time / check_out_time */}
                      <td>{formatTime(log.check_in_time ?? log.check_in)}</td>
                      <td>{formatTime(log.check_out_time ?? log.check_out)}</td>
                      <td>{log.work_hours || "--"}</td>
                      <td>
                        <span
                          className={`${styles.statusChip} ${
                            (log.status || "").toUpperCase() === "PRESENT"
                              ? styles.present
                              : (log.status || "").toUpperCase() === "ABSENT"
                              ? styles.absent
                              : styles.other
                          }`}
                        >
                          {humanizeStatus(log.status)}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            <div
              ref={logsSentinelRef}
              className={styles.infiniteScrollSentinel}
              aria-hidden
            />
            {logsLoadingMore && (
              <div className={styles.infiniteScrollLoading}>
                Loading more...
              </div>
            )}
            {!logsLoading &&
              !logsLoadingMore &&
              !hasMoreLogs &&
              logs.length > 0 && (
                <div className={styles.infiniteScrollEnd}>End of history</div>
              )}
          </div>
        </div>
        {/* RIGHT: Leave Management */}
        <div className={styles.rightCol}>
          {/* Leave Management */}
          <div className={styles.sectionTitle}>Leave Management</div>
          {leaveLoading ? (
            <div className={styles.leaveCard}>Loading...</div>
          ) : (
            <>
              <div className={styles.leaveCard}>
                {/* Show sick/casual leaves here using myDetails */}
                <div
                  className={styles.leaveStatsWrap}
                  style={{ justifyContent: "space-between" }}
                >
                  <div className={styles.leaveStat}>
                    <b>{myDetails?.sick_leave_balance ?? "--"}</b>
                    <div>Sick Leave</div>
                  </div>
                  <div className={styles.leaveStat}>
                    <b>{myDetails?.casual_leave_balance ?? "--"}</b>
                    <div>Casual Leave</div>
                  </div>
                </div>
                <button
                  className={styles.applyLeaveBtn}
                  onClick={() => setShowLeaveModal(true)}
                >
                  + Apply for Leave
                </button>
              </div>

              {/* Applied (Recent) Leaves */}
              {recentLeaves.length > 0 &&
                recentLeaves.map((recentLeave) => (
                  <div
                    className={styles.leaveStatusCard}
                    key={recentLeave.leave_id}
                  >
                    <div className={styles.leaveStatusHeader}>
                      <img
                        src="/Assets/employee.png"
                        className={styles.leaveStatusAvatar}
                        alt="avatar"
                      />
                      <div>
                        <div className={styles.leaveStatusName}>
                          {username}
                          <span
                            className={`${styles.statusBadge} ${
                              styles[recentLeave.status.toLowerCase()]
                            }`}
                          >
                            {recentLeave.status
                              .charAt(0)
                              .toUpperCase() +
                              recentLeave.status.slice(1).toLowerCase()}
                          </span>
                        </div>
                        <div className={styles.leaveType}>
                          {recentLeave.leave_type === "SICK"
                            ? "Sick Leave"
                            : "Casual Leave"}
                        </div>
                      </div>
                      <button
                        className={`${styles.leaveRequestStatusBtn} ${
                          styles[recentLeave.status.toLowerCase()]
                        }`}
                        disabled
                      >
                        {recentLeave.status === "PENDING"
                          ? "Requested"
                          : recentLeave.status === "APPROVED"
                          ? "Approved"
                          : "Rejected"}
                      </button>
                    </div>
                    <div className={styles.leaveStatusDetailRow}>
                      <div>
                        <div className={styles.leaveStatusDetailLabel}>
                          Applied From
                        </div>
                        <div className={styles.leaveStatusDetailValue}>
                          {formatLeaveDate(recentLeave.start_date)}
                        </div>
                      </div>
                      <div>
                        <div className={styles.leaveStatusDetailLabel}>
                          Applied To
                        </div>
                        <div className={styles.leaveStatusDetailValue}>
                          {formatLeaveDate(recentLeave.end_date)}
                        </div>
                      </div>
                      <div>
                        <div className={styles.leaveStatusDetailLabel}>
                          Total Day
                        </div>
                        <div className={styles.leaveStatusDetailValue}>
                          {calcLeaveDays(
                            recentLeave.start_date,
                            recentLeave.end_date
                          )}
                        </div>
                      </div>
                    </div>
                    <div className={styles.leaveStatusReason}>
                      Reason: <b>{recentLeave.reason}</b>
                    </div>
                  </div>
                ))}
            </>
          )}

          {/* Popup modal */}
          {showLeaveModal && (
            <LeaveRequestModal
              onClose={() => setShowLeaveModal(false)}
              onSuccess={() => dispatch(fetchLeaves())}
              availableDays={leaveStats?.available ?? undefined}
            />
          )}

          {/*
          // ================== Task Management (Hidden) ==================
          // The entire Task Management block is intentionally commented out,
          // as requested. Re-enable by uncommenting this section and the
          // related imports/selectors at the top.

          <div className={styles.sectionTitleRow} style={{ marginTop: 28 }}>
            <div className={styles.sectionTitle}>Assigned Tasks</div>
            <span className={styles.activeCountBadge}>
              {tasks.filter((t) => t.status?.toUpperCase() !== "COMPLETED").length} Active
            </span>
          </div>
          <div className={styles.tasksList}>
            {tasksLoading ? (
              <div>Loading tasks...</div>
            ) : tasks.length === 0 ? (
              <div>No tasks assigned.</div>
            ) : (
              tasks.slice(0, 2).map((task) => (
                <div key={task.id} className={styles.taskCard}>
                  <div className={styles.taskHeader}>
                    <div className={styles.taskSubject}>{task.subject}</div>
                    <span
                      className={`${styles.priorityBadge} ${getPriorityClass(
                        task.priority
                      )}`}
                    >
                      {task.priority}
                    </span>
                  </div>
                  <div className={styles.taskMetaRow}>
                    <span
                      className={getStatusClass(task.status)}
                      style={{ fontWeight: 600 }}
                    >
                      {task.status.replace("_", " ")}
                    </span>
                    <span className={styles.taskDue}>
                      Due: {formatLeaveDate(task.end_date)}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
          <button
            className={styles.viewAllTasksBtn}
            onClick={() => {
              if (userId)
                localStorage.setItem("current_view_user_id", String(userId));
              navigate(`/task-management/${userId}`);
            }}
            style={{ width: "100%", marginTop: 10 }}
          >
            View All Tasks
          </button>
          // ================== /Task Management (Hidden) ==================
          */}
        </div>
      </div>
    </div>
  );
};

export default EmployeeAttendance;
